import { webhooks } from '@bb/contracts';
import type { TemplateVariable, WebhookEndpointSummary, WebhookEndpointView, WebhookParam } from '../../../api/client.ts';

/**
 * The `webhook` channel content (BB-142) as the composer reads and writes it: `{ endpointId, params, bodyTemplate? }`.
 * A param's value is a Liquid expression rendered for the recipient, then converted to the param's type; the body
 * template replaces the default JSON body (the typed params) and may read them as `{{ params.<name> }}`.
 */
export interface WebhookContent {
  /** Empty until an endpoint is chosen. */
  endpointId: string;
  params: Record<string, string>;
  bodyTemplate?: string;
}

/** An integrator's full view read as the composer's summary (the list returns either, by permission). */
export const toSummary = (e: WebhookEndpointView | WebhookEndpointSummary): WebhookEndpointSummary =>
  'signed' in e ? e : { id: e.id, name: e.name, description: e.description, origin: e.origin, method: e.method, contentType: e.contentType, params: e.params, signed: e.signingSecretRef !== null, enabled: e.enabled };

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** The stored content with the parts the composer knows; anything else in it is kept by `writeWebhookContent`. */
export function readWebhookContent(raw: unknown): WebhookContent {
  const c = isObject(raw) ? raw : {};
  const params = isObject(c.params) ? Object.fromEntries(Object.entries(c.params).filter((e): e is [string, string] => typeof e[1] === 'string')) : {};
  return { endpointId: typeof c.endpointId === 'string' ? c.endpointId : '', params, ...(typeof c.bodyTemplate === 'string' ? { bodyTemplate: c.bodyTemplate } : {}) };
}

/** The content to store: empty params and an empty body template are left out (an empty param renders empty anyway). */
export function writeWebhookContent(raw: unknown, next: WebhookContent): Record<string, unknown> {
  const { endpointId: _e, params: _p, bodyTemplate: _b, ...rest } = isObject(raw) ? raw : {};
  const params = Object.fromEntries(Object.entries(next.params).filter(([, v]) => v !== ''));
  return { ...rest, ...(next.endpointId ? { endpointId: next.endpointId } : {}), params, ...(next.bodyTemplate ? { bodyTemplate: next.bodyTemplate } : {}) };
}

/** Another endpoint: params it does not declare go (the render would refuse them as `param_unknown`), the rest stay. */
export function switchEndpoint(c: WebhookContent, endpoint: Pick<WebhookEndpointSummary, 'id' | 'params'> | undefined): WebhookContent {
  if (!endpoint) return { ...c, endpointId: '', params: {} };
  const declared = new Set(endpoint.params.map((p) => p.name));
  return { ...c, endpointId: endpoint.id, params: Object.fromEntries(Object.entries(c.params).filter(([name]) => declared.has(name))) };
}

export const PARAM_TYPE_HINTS: Record<WebhookParam['type'], string> = {
  string: 'Text.',
  number: 'A number, e.g. 12.5 — or an expression that renders one.',
  integer: 'A whole number, e.g. 3.',
  boolean: 'true or false.',
  json: 'A JSON value, e.g. {"tier": "gold"} — or an expression that renders one.',
};

export interface WebhookContentIssues {
  endpoint?: string;
  params: Record<string, string>;
  bodyTemplate?: string;
  /** Params stored for names the endpoint does not declare. */
  unknownParams: string[];
}

/**
 * What would stop the send, before any preview: no endpoint, an archived one, a required param left empty, a literal
 * value that is not of the param's type, a body template over 64 KB. Expressions are checked by the preview.
 */
export function webhookContentIssues(c: WebhookContent, endpoint: WebhookEndpointSummary | undefined, loaded: boolean): WebhookContentIssues {
  const issues: WebhookContentIssues = { params: {}, unknownParams: [] };
  if (!c.endpointId) issues.endpoint = 'Choose the endpoint to call.';
  else if (loaded && !endpoint) issues.endpoint = 'This endpoint no longer exists (archived): choose another one.';
  for (const p of endpoint?.params ?? []) {
    const value = (c.params[p.name] ?? '').trim();
    if (!value) {
      if (p.required) issues.params[p.name] = 'Required.';
      continue;
    }
    if (value.includes('{{') || value.includes('{%')) continue;
    if (p.type === 'number' && !Number.isFinite(Number(value))) issues.params[p.name] = 'Not a number.';
    if (p.type === 'integer' && !/^-?\d+$/.test(value)) issues.params[p.name] = 'Not a whole number.';
    if (p.type === 'boolean' && value !== 'true' && value !== 'false') issues.params[p.name] = 'Enter true or false.';
    if (p.type === 'json') {
      try {
        JSON.parse(value);
      } catch {
        issues.params[p.name] = 'Not valid JSON.';
      }
    }
  }
  if (endpoint) issues.unknownParams = Object.keys(c.params).filter((name) => !endpoint.params.some((p) => p.name === name));
  if (c.bodyTemplate && new TextEncoder().encode(c.bodyTemplate).length > webhooks.WEBHOOK_BODY_MAX_BYTES) issues.bodyTemplate = 'At most 64 KB.';
  return issues;
}

export const hasIssues = (i: WebhookContentIssues) => Boolean(i.endpoint || i.bodyTemplate || i.unknownParams.length || Object.keys(i.params).length);

const VARIABLE_TYPES: Record<WebhookParam['type'], TemplateVariable['type']> = { string: 'string', number: 'number', integer: 'number', boolean: 'boolean', json: 'object' };

/**
 * The endpoint's params as personalization variables of the body template (namespace `params`). In a JSON body a
 * string param goes through `| json`, so quotes and escapes come out right.
 */
export function paramVariables(endpoint: WebhookEndpointSummary | undefined): TemplateVariable[] {
  if (!endpoint) return [];
  const json = endpoint.contentType === 'application/json';
  return endpoint.params.map((p) => ({
    namespace: 'params',
    path: p.name,
    type: VARIABLE_TYPES[p.type],
    piiLevel: 'none',
    liquid: json ? `{{ params.${p.name} | json }}` : `{{ params.${p.name} }}`,
    description: [p.required ? 'required' : 'optional', p.description].filter(Boolean).join(' · '),
  }));
}
